import {
  CTypeMap,
  DTypeTabsFacility,
  DTypeCTA,
  DefaultFacility,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  buildFacilityPageSchema,
  GOI_WEB_URL,
  RichSearch,
  ParlorHeadSection,
  ATypeFacilityList,
  ATypeFacilityMainHead,
  BTypeBreadCrumb,
  ATypeFacilityFAQ,
  FacilityDetailFAQ,
  ATypeCardView,
  useFacilities,
  ClickEventType,
  excludeFacilities,
  FacilityHiddenNotice,
  getFacilityTypeLabel,
  getFacilityDisplayName,
  getFacilityRegionKeyword,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";

import facilities from "goi_common/src/constants/facilities_detail.json";

import { parlorRegion } from "@/constants/parlorRegion";
import { Stack } from "@mui/material";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = facilities
    .filter((facility) => targetSidos.includes(facility.sido))
    .map((facility) => ({
      params: {
        id: String(facility.id),
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id as string;

  const p_facility = facilities.find(
    (facility) => Number(facility.id) === Number(id)
  ) as DefaultFacility;

  const { address, companyname, images, type } = p_facility;
  const typeLabel = getFacilityTypeLabel(type);
  const displayName = getFacilityDisplayName(companyname, type);
  const regionKeyword = getFacilityRegionKeyword(type);

  const 시가아닌_도인경우 = address.split(" ")[0].length == 4;
  const district = 시가아닌_도인경우
    ? address[0] + address[2]
    : address.slice(0, 2);

  const title = `[${district}] ${displayName} 정보 한눈에 정리 | 대구경북장례정보포털`;
  const description = `${companyname}의 ${typeLabel} 정보를 한눈에 정리하여 제공하고 있습니다. 찾아 가시는 길 ${address} | ${companyname} 정보, 비용`;
  const keywords = `${district} ${regionKeyword}, ${companyname}, ${companyname} 정보, ${companyname} 비용, ${companyname} 가격, ${companyname} 예약, ${companyname} 위치, ${companyname} 시설, ${district} 장지 비용, ${district} 공설장지, ${district} 장례 혜택`;

  return {
    props: {
      defaultFacility: p_facility,
      pageDescription: description,
      pageKeywords: keywords,
      pageOgTitle: title,
      pageOgImage: images[0].url || "",
    },
  };
};

interface FacilityDetailPagProps {
  pageOgTitle: string;
  defaultFacility: DefaultFacility;
}

const FacilityDetailPage = ({
  pageOgTitle,
  defaultFacility,
}: FacilityDetailPagProps) => {
  const sidoName = FUNERAL_SIDO.get(Number(defaultFacility.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultFacility.sido))?.get(
    Number(defaultFacility.sigungu)
  );

  const facilityPageSchema = buildFacilityPageSchema(defaultFacility);

  const { facilityList, facilityPrices } = useFacilities({
    sido: defaultFacility.sido,
  });

  const currentPageEvent: ClickEventType = {
    domain: "dggb",
    path: "facility",
  };

  const isHidden = excludeFacilities.includes(String(defaultFacility.id));

  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>

        {/* <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqList) }}
        /> */}
      </Head>
      <RichSearch data={facilityPageSchema} />

      <BTypeBreadCrumb
        firstItem={{
          text: `장지 추천`,
          href: isHidden
            ? `${GOI_WEB_URL}/facilities/search/initial/?referrerId=${defaultFacility.id}&utm_source=dogsound`
            : `${GOI_WEB_URL}/match/facility-requests/${defaultFacility.id}/?utm_source=dogsound`,
          event: {
            domain: "dggb",
            path: "facility",
            trigger_and_target: "br_crumb_mth_facil_req",
          },
        }}
        // secondItem={{
        //   text: `${FUNERAL_SIDO.get(Number(defaultParlor.sido))} 장례식장`,
        //   href: `/price/${defaultParlor.sido}/`,
        //   event: {
        //     domain: "dggb",
        //     path: event.path,
        //     trigger_and_target: "br_crumb_sido_prlr_list",
        //   },
        // }}
        // thirdItem={{
        //   text: `${FUNERAL_SIGUNGU.get(Number(defaultParlor.sido))?.get(
        //     Number(defaultParlor.sigungu)
        //   )} 장례식장`,
        //   href: `/price/${defaultParlor.sido}-${defaultParlor.sigungu}/`,
        //   event: {
        //     domain: "dggb",
        //     path: event.path,
        //     trigger_and_target: "br_crumb_sigungu_prlr_list",
        //   },
        // }}
        goiHomeEvent={{
          ...currentPageEvent,
          trigger_and_target: "br_crumb_goi_home",
        }}
      />

      <ParlorHeadSection
        component={
          <>
            <ATypeFacilityMainHead
              sidoName={sidoName}
              sigunguName={sigunguName}
              defaultFacility={defaultFacility}
              title={`${defaultFacility.companyname}`}
            />
          </>
        }
      />
      {isHidden ? (
        <FacilityHiddenNotice
          companyname={defaultFacility.companyname}
          mainColor={regionColor}
          ctaHref={`${GOI_WEB_URL}/facilities/search/initial/?referrerId=${defaultFacility.id}&utm_source=dogsound`}
          event={{
            ...currentPageEvent,
            trigger_and_target: "CTA_uv_facil_req",
          }}
        />
      ) : (
        <>
      <Stack
        style={{
          background: regionColor.c100,
          padding: "40px 20px",
        }}
      >
        <DTypeCTA
          mainColor={regionColor}
          text="장지 비용 알아보기"
          companyname={defaultFacility.companyname}
          href={`${GOI_WEB_URL}/match/facility-requests/${defaultFacility.id}/?utm_source=dogsound`}
          event={{
            ...currentPageEvent,
            trigger_and_target: "CTA_uv_facil_req",
          }}
        />
      </Stack>
      <Stack style={{ background: "white" }}>
        <DTypeTabsFacility
          mainColor={regionColor}
          href={`facility-place/${defaultFacility.id}/`}
          selectTab="소개"
          event={{
            ...currentPageEvent,
            trigger_and_target: "CType_facil_tab",
          }}
        />
      </Stack>

      <Stack padding={"40px 20px"}>
        <ATypeFacilityFAQ
          defaultFacility={defaultFacility}
          event={currentPageEvent}
        />
        <FacilityDetailFAQ />
      </Stack>

      <Stack
        style={{
          background: regionColor.c100,
          padding: "40px 20px",
        }}
      >
        <CTypeMap defaultParlor={defaultFacility} />
      </Stack>
        </>
      )}

      <Stack style={{ background: regionColor.c100, padding: "20px 0px" }}>
        <ATypeFacilityList
          title={""}
          // sido={defaultFacility.sido}
          // title="주변 장지"
          // event={{
          //   domain: "dggb",
          //   path: "funeral_place",
          //   trigger_and_target: "uv_prlr_detail",
          // }}
          // defaultHref={"/facility-place"}
          // mainColor={regionColor}
        >
          <ATypeFacilityList title="주변 장지">
            {facilityList.length > 0 &&
              facilityList?.map((res, index) => {
                const { max, min } = facilityPrices[index];
                const averagePrice = (max + min) / 2;

                return (
                  <ATypeCardView
                    key={res.companyname}
                    name={res.companyname}
                    href={`/facility-place/${res.id}/`}
                    arrowText={`${getFacilityDisplayName(res.companyname, res.type)} 알아보기`}
                    descriptions={[
                      `장지 평균 비용 : ${averagePrice.toLocaleString()}원`,
                    ]}
                    imgSrc={res.images?.[0].url || ""}
                    event={{
                      ...currentPageEvent,
                      trigger_and_target: "uv_facility",
                    }}
                  />
                );
              })}
          </ATypeFacilityList>
        </ATypeFacilityList>
      </Stack>
    </>
  );
};

export default FacilityDetailPage;
